import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useAppDispatch } from "src/store";
import { showDevelopmentError, showToastNotification } from "src/utils";
import {
  fetchAllLocations,
  fetchAllLanguages,
  fetchThreadsByUserId,
  updateCompanyMembersWithEmail,
} from "src/store/actions";
import {
  selectUserId,
  selectCompanyId,
  selectThreadsStatus,
  selectLocationsStatus,
  selectLanguagesStatus,
  selectIsCompanyMembersEmailLoaded,
} from "src/store/selectors";

const agentUrl = process.env.REACT_APP_LANGGRAPH_URL || "";

export const useLocationsLoader = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useSelector(selectUserId);

  const locationsStatus = useSelector(selectLocationsStatus);

  const isLocationsLoaded = useMemo<boolean>(
    () => locationsStatus !== "idle",
    [locationsStatus],
  );

  useEffect(() => {
    if (!userId || isLocationsLoaded) return;

    dispatch(fetchAllLocations())
      .unwrap()
      .catch((error) => {
        console.error(error);

        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
      });
  }, [userId, isLocationsLoaded, dispatch, t]);
};

export const useLanguagesLoader = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useSelector(selectUserId);

  const languagesStatus = useSelector(selectLanguagesStatus);

  const isLanguagesLoaded = useMemo<boolean>(
    () => languagesStatus !== "idle",
    [languagesStatus],
  );

  useEffect(() => {
    if (!userId || isLanguagesLoaded) return;

    dispatch(fetchAllLanguages())
      .unwrap()
      .catch((error) => {
        console.error(error);

        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
      });
  }, [userId, isLanguagesLoaded, dispatch, t]);
};

export const useCompanyMembersEmailLoader = (): boolean => {
  const dispatch = useAppDispatch();

  const companyId = useSelector(selectCompanyId);

  const isMembersEmailLoaded = useSelector(selectIsCompanyMembersEmailLoaded);

  useEffect(() => {
    if (!companyId || isMembersEmailLoaded) return;

    dispatch(updateCompanyMembersWithEmail())
      .unwrap()
      .catch((error) => {
        const errorTitle = "ERROR IN MEMBERS EMAIL FETCHING";

        showDevelopmentError({ additionalTexts: [errorTitle], error });
      });
  }, [companyId, isMembersEmailLoaded, dispatch]);

  return isMembersEmailLoaded;
};

export const useUserThreadsLoader = (): boolean => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useSelector(selectUserId);

  const threadsStatus = useSelector(selectThreadsStatus);

  const isThreadsLoaded = useMemo<boolean>(
    () => threadsStatus !== "idle",
    [threadsStatus],
  );

  useEffect(() => {
    if (isThreadsLoaded || !userId) return;

    dispatch(fetchThreadsByUserId({ userId, agentUrl }))
      .unwrap()
      .catch((error) => {
        console.error(error);

        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
      });
  }, [userId, isThreadsLoaded, dispatch, t]);

  return isThreadsLoaded;
};
